import React, { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS } from '../TemplateEditor/mergeFieldDefinitions';

// Direct imports rather than via the components barrel to dodge HMR cycle
// issues during dev — same rationale documented on CreateTemplateModal.js.
import Button from '../Button/Button';
import Modal from '../Modal/Modal';

import css from './ForkTemplateModal.module.css';

/**
 * ForkTemplateModal — Push 9 Sub-phase B polish.
 *
 * Replaces the prior "Duplicate" row-action confirm dialog. Lets the
 * provider override name + document_type + signer_role at fork time so
 * a Check-in inspection can be cloned into a Check-out (or any other
 * cross-type repurposing). The pure-duplicate use case is unchanged
 * behavior — caller leaves the defaults untouched.
 *
 * Form state is local (controlled inputs), matching CreateTemplateModal's
 * pattern. Three fields + cancel/submit:
 *   - name (defaults to "Copy of <original name>")
 *   - document_type (defaults to original's type)
 *   - signer_role (defaults to original's role; auto-locked for single-
 *     signer-role types per VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS)
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Object|null} props.source — the original template row; reads
 *   { id, name, document_type, signer_role } to seed defaults
 * @param {Function} props.onClose
 * @param {Function} props.onManageDisableScrolling
 * @param {(values: { templateId, newName, newDocumentType, newSignerRole }) => Promise} props.onSubmit
 * @param {boolean} [props.isSubmitting]
 * @param {Object|null} [props.error]
 */
const DOCUMENT_TYPES = [
  'contract',
  'waiver',
  'agreement',
  'inspection_pre_departure',
  'inspection_post_return',
];
const NAME_MAX_LENGTH = 255;

const isSingleSignerRoleType = documentType =>
  documentType === 'contract' ||
  documentType === 'inspection_pre_departure' ||
  documentType === 'inspection_post_return';

const ForkTemplateModal = props => {
  const intl = useIntl();
  const {
    isOpen = false,
    source,
    onClose,
    onManageDisableScrolling,
    onSubmit,
    isSubmitting = false,
    error,
  } = props;

  // Seed defaults from `source` whenever the modal opens with a new
  // source. Closing the modal resets to empty strings so a reopen with a
  // different source doesn't briefly flash the previous template's
  // values. The seed effect re-fires on every source change while
  // isOpen is true.
  const [name, setName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [signerRole, setSignerRole] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDocumentType('');
      setSignerRole('');
      return;
    }
    if (source) {
      const sourceName = source.name || '';
      // "Copy of <Original Template Name>" — clipped if the original is
      // close to the 255-char limit so the prefix doesn't push the new
      // name over (server rejects on NAME_TOO_LONG).
      const prefix = 'Copy of ';
      const maxBase = NAME_MAX_LENGTH - prefix.length;
      const trimmedBase = sourceName.length > maxBase ? sourceName.slice(0, maxBase) : sourceName;
      setName(`${prefix}${trimmedBase}`);
      setDocumentType(source.document_type || '');
      setSignerRole(source.signer_role || '');
    }
  }, [isOpen, source]);

  // Auto-clamp signer_role when document_type changes such that the
  // current role is no longer valid for the new type. For single-signer-
  // role types (contract + both inspection types) we force 'renter'.
  // Mirrors CreateTemplateModal's effect logic.
  useEffect(() => {
    if (!documentType) return;
    if (isSingleSignerRoleType(documentType)) {
      setSignerRole('renter');
      return;
    }
    const validRoles = VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS[documentType] || [];
    if (signerRole && !validRoles.includes(signerRole)) {
      setSignerRole('');
    }
    // Intentionally only react to documentType changes — including
    // signerRole would loop on the setSignerRole calls. The closure
    // reads the latest signerRole at effect-fire time, which is what
    // we want. Mirrors CreateTemplateModal's equivalent useEffect.
  }, [documentType]);

  const validSignerRoles = documentType
    ? VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS[documentType] || []
    : [];
  const lockedSignerRole = isSingleSignerRoleType(documentType);

  const trimmedName = name.trim();
  const nameValid = trimmedName.length > 0 && trimmedName.length <= NAME_MAX_LENGTH;
  const canSubmit = !!source?.id && nameValid && documentType && signerRole && !isSubmitting;

  const handleSubmit = e => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      templateId: source.id,
      newName: trimmedName,
      newDocumentType: documentType,
      newSignerRole: signerRole,
    });
  };

  return (
    <Modal
      id="ForkTemplateModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'ForkTemplateModal.close' })}
    >
      <h2 className={css.modalTitle}>
        <FormattedMessage id="ForkTemplateModal.title" />
      </h2>

      {source?.name ? (
        <p className={css.originalLine}>
          <FormattedMessage id="ForkTemplateModal.originalLine" values={{ name: source.name }} />
        </p>
      ) : null}

      {error ? (
        <p className={css.errorLine}>
          <FormattedMessage
            id="ForkTemplateModal.forkError"
            values={{ error: error.message || error.toString() }}
          />
        </p>
      ) : null}

      <form className={css.form} onSubmit={handleSubmit} noValidate>
        <label className={css.field}>
          <span className={css.label}>
            <FormattedMessage id="ForkTemplateModal.nameLabel" />
          </span>
          <input
            type="text"
            className={css.input}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={intl.formatMessage({ id: 'ForkTemplateModal.namePlaceholder' })}
            autoFocus
            required
          />
          {trimmedName.length > NAME_MAX_LENGTH ? (
            <span className={css.fieldError}>
              <FormattedMessage id="ForkTemplateModal.nameTooLong" />
            </span>
          ) : null}
        </label>

        <label className={css.field}>
          <span className={css.label}>
            <FormattedMessage id="ForkTemplateModal.documentTypeLabel" />
          </span>
          <select
            className={css.select}
            value={documentType}
            onChange={e => setDocumentType(e.target.value)}
            required
          >
            {DOCUMENT_TYPES.map(t => (
              <option key={t} value={t}>
                {intl.formatMessage({ id: `CreateTemplateModal.documentType.${t}` })}
              </option>
            ))}
          </select>
        </label>

        {lockedSignerRole ? (
          <div className={css.field}>
            <span className={css.label}>
              <FormattedMessage id="ForkTemplateModal.signerLabel" />
            </span>
            <div className={css.lockedField} aria-disabled="true">
              <span className={css.lockIcon} aria-hidden="true">
                🔒
              </span>
              <span className={css.lockedFieldText}>
                <FormattedMessage id="CreateTemplateModal.signerRole.renter" />
              </span>
            </div>
          </div>
        ) : (
          <label className={css.field}>
            <span className={css.label}>
              <FormattedMessage id="ForkTemplateModal.signerLabel" />
            </span>
            <select
              className={css.select}
              value={signerRole}
              onChange={e => setSignerRole(e.target.value)}
              disabled={!documentType}
              required
            >
              {!signerRole ? (
                <option value="">
                  {intl.formatMessage({ id: 'ForkTemplateModal.selectPlaceholder' })}
                </option>
              ) : null}
              {validSignerRoles.map(r => (
                <option key={r} value={r}>
                  {intl.formatMessage({ id: `CreateTemplateModal.signerRole.${r}` })}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className={css.buttons}>
          <Button
            type="button"
            className={css.cancelButton}
            onClick={onClose}
            disabled={isSubmitting}
          >
            <FormattedMessage id="ForkTemplateModal.cancelButton" />
          </Button>
          <Button
            type="submit"
            className={css.submitButton}
            disabled={!canSubmit}
            inProgress={isSubmitting}
          >
            <FormattedMessage
              id={
                isSubmitting
                  ? 'ForkTemplateModal.submittingButton'
                  : 'ForkTemplateModal.submitButton'
              }
            />
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ForkTemplateModal;
